import { useState } from "react";
import { motion } from "framer-motion";
import { Video, Phone, Send, CheckCircle2, Mail, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const TIMES = ["09:00", "10:00", "11:00", "12:00", "14:00", "15:00", "16:00", "17:00", "18:00"];

const inputCls =
  "w-full rounded-xl bg-ink border border-white/10 px-4 py-3 text-sm text-[#F8F6F0] placeholder:text-zinc-600 focus:outline-none focus:border-gold/60 focus:ring-1 focus:ring-gold/40 transition-colors";

export const Booking = () => {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", category: "undecided",
    mode: "zoom", date: "", time: "10:00", message: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/appointments", form);
      setDone(data);
      toast.success("Request received — we'll confirm shortly.");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not submit. Please check the fields and try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="booking" data-testid="booking-section" className="py-20 lg:py-32 scroll-mt-20 bg-ink-card/30 border-y border-white/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-14">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
        >
          <p className="text-xs uppercase tracking-[0.35em] text-gold-light/90 font-medium mb-6">Book an appointment</p>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#F8F6F0] leading-tight">
            Thirty minutes with an advisor. <span className="italic gold-text">Zoom or phone.</span>
          </h2>
          <p className="mt-6 text-sm sm:text-base text-zinc-300 leading-relaxed max-w-lg">
            Tell us what you're exploring and how you'd like to meet. We confirm every request personally —
            with a Zoom link or a call at your chosen time.
          </p>
          <div className="mt-10 space-y-4 text-sm text-zinc-300">
            <a href="mailto:support@havenandhillproperties.com" data-testid="booking-email-link"
               className="flex items-center gap-3 hover:text-gold-light transition-colors">
              <span className="w-10 h-10 rounded-xl bg-gold/10 border border-gold/25 flex items-center justify-center"><Mail size={16} className="text-gold" /></span>
              support@havenandhillproperties.com
            </a>
            <a href="https://wa.me/971588123628" target="_blank" rel="noopener noreferrer" data-testid="booking-whatsapp-link"
               className="flex items-center gap-3 hover:text-gold-light transition-colors">
              <span className="w-10 h-10 rounded-xl bg-gold/10 border border-gold/25 flex items-center justify-center"><MessageCircle size={16} className="text-gold" /></span>
              WhatsApp +971 58 812 3628
            </a>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.1 }}
        >
          {done ? (
            <div className="gold-ring rounded-3xl bg-ink-card p-10 text-center h-full flex flex-col items-center justify-center" data-testid="booking-success">
              <CheckCircle2 size={44} className="text-gold" />
              <h3 className="mt-6 font-serif text-2xl text-[#F8F6F0]">Request received</h3>
              <p className="mt-3 text-sm text-zinc-300 leading-relaxed max-w-sm">
                Thank you, {form.name.split(" ")[0]}. Our team will confirm your{" "}
                {form.mode === "zoom" ? "Zoom video call" : "phone call"} for {form.date} at {form.time} (GST)
                by email or WhatsApp shortly.
              </p>
              <button
                data-testid="booking-another-btn"
                onClick={() => setDone(null)}
                className="mt-8 text-xs tracking-wide text-gold-light hover:text-gold-pale transition-colors"
              >
                Book another appointment
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="gold-ring rounded-3xl bg-ink-card p-7 sm:p-9 space-y-5" data-testid="booking-form">
              <div className="grid grid-cols-2 gap-3">
                {[
                  { key: "zoom", icon: Video, label: "Zoom video call" },
                  { key: "phone", icon: Phone, label: "Phone call" },
                ].map((m) => (
                  <button
                    type="button"
                    key={m.key}
                    data-testid={`booking-mode-${m.key}`}
                    onClick={() => setForm({ ...form, mode: m.key })}
                    className={`rounded-xl border p-4 flex flex-col items-center gap-2 text-xs font-medium transition-all ${
                      form.mode === m.key
                        ? "border-gold/60 bg-gold/10 text-gold-pale"
                        : "border-white/10 text-zinc-400 hover:border-gold/30"
                    }`}
                  >
                    <m.icon size={18} className={form.mode === m.key ? "text-gold" : "text-zinc-500"} />
                    {m.label}
                  </button>
                ))}
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <input required data-testid="booking-name-input" placeholder="Full name" value={form.name} onChange={set("name")} className={inputCls} />
                <input required type="email" data-testid="booking-email-input" placeholder="Email" value={form.email} onChange={set("email")} className={inputCls} />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <input required data-testid="booking-phone-input" placeholder="Phone / WhatsApp" value={form.phone} onChange={set("phone")} className={inputCls} />
                <select data-testid="booking-category-select" value={form.category} onChange={set("category")} className={inputCls}>
                  <option value="undecided">I'm exploring</option>
                  <option value="investor">I'm investing</option>
                  <option value="end_user">I'm buying a home</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <input required type="date" data-testid="booking-date-input" min={new Date().toISOString().split("T")[0]}
                       value={form.date} onChange={set("date")} className={`${inputCls} [color-scheme:dark]`} />
                <select data-testid="booking-time-select" value={form.time} onChange={set("time")} className={inputCls}>
                  {TIMES.map((t) => <option key={t} value={t}>{t} GST</option>)}
                </select>
              </div>
              <textarea data-testid="booking-message-input" rows={3} placeholder="What would you like to discuss? (optional)"
                        value={form.message} onChange={set("message")} className={`${inputCls} resize-none`} />
              <button
                type="submit"
                disabled={busy}
                data-testid="appointment-submit-btn"
                className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-gold px-7 py-3.5 text-sm font-semibold text-ink hover:bg-gold-hover disabled:opacity-60 transition-colors"
              >
                {busy ? "Sending…" : "Request appointment"} <Send size={15} />
              </button>
              <p className="text-[11px] text-zinc-600 text-center">
                We reply within one business day. Your details are never shared.
              </p>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
};
